import * as React from 'react';
import { HierarchyOptions } from './hierarchy-options/hierarchy-options';
import { FlatOptions } from './flat-options/flat-options';
import { SelectType, SelectItem, OptionsType, OptionsDisplaySetting } from './types';
import { Options, ComplexValue, LoadOptions } from '../types';
import { IndexOptions } from './index-options/index-options';
import { maxOptionsPerPageLimit, showIndexWhenItemCountIsAbove } from './constants';
import { isHierarchy } from './select-type';
import styles from './options-resolver.scss';
import { CategoryOptions } from 'ui/common/select/category-options/category-options';
import { BreadcrumbValue } from 'ui/common/breadcrumb/breadcrumb-item';

export interface OptionsResolverProps extends Options<number | string> {
  valueItems: SelectItem[];
  selectType: SelectType;
  minLevel: number;
  levelsNotAllowed?: boolean;
  leafNodeOnly: boolean;
  descriptionAllLevels: boolean;
  optionsDisplaySetting?: OptionsDisplaySetting;
  showIcons: boolean;
  showLongDescription: boolean;
  searchRequired?: boolean;
  notEnoughInputWhenSearchRequired?: boolean;
  setCategoryId: (categoryId: number) => void;
  showBackgroundColour?: boolean;

  switchButton?: JSX.Element;
  displaySelectAll?: boolean;
  returnedOptions: ComplexValue<number>[];
  returnedCategoryOptions: ComplexValue<number>[];
  allLevelsDescriptionPrefix: string;
  breadcrumbStack: BreadcrumbValue[];
  breadcrumb: JSX.Element;

  textSearchRequired?: string;
  textSearchRequiredExceedsMaxResult?: string;
  textSelectAll: string;
  textClearAll: string;
  textPageOf?: string;

  onSelectOption: (item: SelectItem) => void;
  onChange: (items: SelectItem[]) => void;
  showIndexWhenNoOfPagesIsAbove?: number;
  showCategoryOptions?: boolean;

  onLoadOptions?: (loadOptions: LoadOptions) => void;
  includeDisabledCheckbox?: JSX.Element;
}

export class OptionsResolver extends React.PureComponent<OptionsResolverProps> {

  private searchRequiredOptionsLimit: number = 500;

  private setTypeIfOptionTypeAuto = (type: OptionsType, itemsLength: number, showIndexWhenNoOfPagesIsAbove?: number) => {
    const { indexMin = showIndexWhenItemCountIsAbove } = this.props?.optionsDisplaySetting ?? {};
    const paginatedOptionsIndexMin = !!showIndexWhenNoOfPagesIsAbove ? showIndexWhenNoOfPagesIsAbove * maxOptionsPerPageLimit : undefined;
    if (type === OptionsType.auto) {
      return (itemsLength > indexMin || itemsLength > paginatedOptionsIndexMin)
        ? OptionsType.index
        : OptionsType.flat;
    }
    return type;
  };

  public exceedsMaxResultWhenSearchRequired = (options: ComplexValue<string | number>[]) => {
    return this.props.searchRequired && options?.length > this.searchRequiredOptionsLimit;
  };

  public render() {

    const { type = OptionsType.auto } = this.props?.optionsDisplaySetting ?? {};
    
    const { 
      loadOptions, selectType, textSearchRequired, textSearchRequiredExceedsMaxResult, options,  
      notEnoughInputWhenSearchRequired, returnedOptions, returnedCategoryOptions, showIndexWhenNoOfPagesIsAbove, 
      breadcrumb, onLoadCategoryOptions, showCategoryOptions
    } = this.props;

    const { query = '' } = loadOptions;
    const displaySearchRequiredErrorFromOptions: boolean = notEnoughInputWhenSearchRequired ||
      this.exceedsMaxResultWhenSearchRequired(options);
    const searchRequiredErrorMessage = notEnoughInputWhenSearchRequired ? textSearchRequired :
      this.exceedsMaxResultWhenSearchRequired ? textSearchRequiredExceedsMaxResult : '';
    const searchRequiredErrorOptions = !!displaySearchRequiredErrorFromOptions &&
      <div className={styles.errorMessage}>{searchRequiredErrorMessage}</div>;
          
    if (showCategoryOptions) {
      return <CategoryOptions 
              {...this.props} 
              onLoadCategoryOptions={onLoadCategoryOptions}
              returnedCategoryOptions={returnedCategoryOptions} 
              breadcrumb={breadcrumb} />;
    }

    if (isHierarchy(selectType)) {
      if (!query) {
        const displaySearchRequiredErrorFromReturnedOptions: boolean = notEnoughInputWhenSearchRequired
          || this.exceedsMaxResultWhenSearchRequired(returnedOptions);

        const searchRequiredErrorReturnedOptions = !!displaySearchRequiredErrorFromReturnedOptions &&
          <div className={styles.errorMessage}>{searchRequiredErrorMessage}</div>;

        return searchRequiredErrorReturnedOptions || <HierarchyOptions {...this.props} breadcrumb={breadcrumb} />;
      } else {
        return searchRequiredErrorOptions || <FlatOptions {...this.props} isSearch={true} breadcrumb={breadcrumb} />;
      }
    } else {
      const optionsType: OptionsType = this.setTypeIfOptionTypeAuto(type, this.props.options.length, showIndexWhenNoOfPagesIsAbove);
    
      if (optionsType === OptionsType.flat) {
        return searchRequiredErrorOptions || <FlatOptions {...this.props} isSearch={!!query} breadcrumb={breadcrumb} />;
      } else {
        return searchRequiredErrorOptions || <IndexOptions {...this.props} />;
      }
    }
  }
}
